import React from 'react'
import { observer } from 'mobx-react'
import { productState } from 'src/views/components/BaseProductComponent/ProductState'
import { currencyFormat, i18nTranslate } from 'src/utils'
import './styles.scss'

/**
 * PriorPrice Component
 *
 * Displays the Prior Price as "Lowest price in last 30 days $XX.XX" on PDP when:
 * - Prior price data is available in product/sku properties
 * - The current price (after promotion) is lower than prior price
 *
 * Note: Feature flag check (priorPricing) is handled by parent component
 *
 * For normal products: reads from selectedSku.properties.lowestPriorPrice
 * For bundles/kits: reads from product.properties.lowestPriorPrice
 */
@observer
class PriorPrice extends React.Component {
  /**
   * Get the prior price value from product or SKU properties based on product type.
   * @returns {number|null} The prior price value or null if not available
   */
  getPriorPriceValue = () => {
    const { product = {}, isBundle = false } = this.props

    if (isBundle) {
      // For bundles/kits: prior price is at product header level
      if (this.checkIfOneTimePrice()) {
        return parseFloat(product?.properties?.lowestPriorPrice) || null
      } else {
        return parseFloat(product?.properties?.subLowestPriorPrice) || null
      }
    }

    // For normal products: prior price is at SKU level
    const selectedSku = product?.selectedSku || product?.sku?.[0] || {}
    if (this.checkIfOneTimePrice()) {
      return parseFloat(selectedSku?.properties?.lowestPriorPrice) || null
    } else {
      return parseFloat(selectedSku?.properties?.subLowestPriorPrice) || null
    }
  }

  checkIfOneTimePrice = () => {
    if (this.props?.showSubscriptionPrice) return false
    if (productState?.currentlySelectedPriceOption == 'one-time-price-button') {
      return true
    }
    return false
  }

  /**
   * Get the current price (after any promotions/discounts).
   * @returns {number} The current effective price
   */

  /**
   * Check if prior price should be displayed.
   * Conditions:
   * - Prior price value exists and is valid
   * - Prior price is greater than the current effective price
   */
  shouldShowPriorPrice = () => {
    const priorPrice = this.getPriorPriceValue()
    if (!priorPrice || priorPrice <= 0) {
      return false
    }

    return true
  }

  render() {
    if (!this.shouldShowPriorPrice()) {
      return null
    }

    const priorPrice = this.getPriorPriceValue()
    const formattedPriorPrice = currencyFormat({ price: priorPrice })

    return (
      <div
        className="prior-price d-flex align-items-center"
        data-testid="qa-prior-price">
        <span className="prior-price-label">
          {i18nTranslate('pdp.priorPrice', 'Lowest price in last 30 days', {
            nameSpace: 'ssr-resource',
          })}
        </span>
        <span
          className="prior-price-value ml-1"
          data-testid="qa-prior-price-value">
          {formattedPriorPrice}
        </span>
      </div>
    )
  }
}

export { PriorPrice }
